import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Star } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Choice = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-testid={`${testid}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
          value === o.value
            ? "bg-[#0A192F] text-white shadow-[0_4px_14px_rgba(10,25,47,0.25)]"
            : "bg-slate-50 border border-slate-200 text-slate-600 hover:border-slate-400"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function Riscontro() {
  const params = new URLSearchParams(window.location.search);
  const requestId = params.get("r") || "";
  const [contattato, setContattato] = useState("");
  const [proposte, setProposte] = useState("");
  const [appuntamento, setAppuntamento] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [testo, setTesto] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!requestId) {
      toast.error("Link non valido: manca il riferimento alla richiesta");
      return;
    }
    if (!contattato || !proposte || !appuntamento) {
      toast.error("Rispondi alle tre domande per continuare");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, {
        request_id: requestId,
        contattato,
        proposte,
        appuntamento,
        rating: rating || null,
        testo,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio, riprova");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-5 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <LogoMark size={48} />
          <p className="text-xs text-slate-500 mt-3">La casa cerca te! · powered by Casamatch.org</p>
        </div>

        {done ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-10 text-center" data-testid="feedback-success">
            <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-[#0A192F]">Grazie mille!</h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              La tua risposta ci aiuta a far funzionare sempre meglio il matching. In bocca al lupo per la tua casa!
            </p>
            <a href="/" className="inline-block mt-6 text-sm font-bold text-[#10B981] hover:underline">
              ← Torna alla home
            </a>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="feedback-form"
            className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-8 space-y-7"
          >
            <div className="text-center">
              <h1 className="font-display text-2xl font-extrabold text-[#0A192F]">Ti hanno trovato casa?</h1>
              <p className="text-sm text-slate-500 mt-2">Tre domande, 30 secondi. La tua richiesta resta riservata.</p>
            </div>

            <div className="space-y-2.5">
              <Label className="text-sm font-semibold text-[#0A192F]">1 · Sei stato contattato?</Label>
              <Choice
                testid="feedback-contattato"
                value={contattato}
                onChange={setContattato}
                options={[
                  { value: "si", label: "Sì, mi hanno contattato" },
                  { value: "no", label: "No, non ancora" },
                ]}
              />
            </div>

            <div className="space-y-2.5">
              <Label className="text-sm font-semibold text-[#0A192F]">2 · Hai ricevuto proposte in linea con la tua richiesta?</Label>
              <Choice
                testid="feedback-proposte"
                value={proposte}
                onChange={setProposte}
                options={[
                  { value: "tante", label: "Sì, diverse" },
                  { value: "alcune", label: "Sì, qualcuna" },
                  { value: "nessuna", label: "No, nessuna" },
                ]}
              />
            </div>

            <div className="space-y-2.5">
              <Label className="text-sm font-semibold text-[#0A192F]">3 · Hai fissato un appuntamento o visitato un immobile?</Label>
              <Choice
                testid="feedback-appuntamento"
                value={appuntamento}
                onChange={setAppuntamento}
                options={[
                  { value: "visita_fatta", label: "Sì, visita già fatta" },
                  { value: "fissato", label: "Sì, appuntamento fissato" },
                  { value: "non_ancora", label: "Non ancora" },
                ]}
              />
            </div>

            <div className="space-y-2.5">
              <Label className="text-sm font-semibold text-[#0A192F]">
                Come valuti il servizio? <span className="font-normal text-slate-400">(facoltativo)</span>
              </Label>
              <div className="flex gap-1.5" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`feedback-star-${n}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="transition-transform duration-150 hover:scale-110"
                  >
                    <Star className={`w-8 h-8 ${n <= (hover || rating) ? "fill-[#10B981] text-[#10B981]" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2.5">
              <Label htmlFor="fb-testo" className="text-sm font-semibold text-[#0A192F]">
                Vuoi dirci qualcosa in più? <span className="font-normal text-slate-400">(facoltativo)</span>
              </Label>
              <Textarea
                id="fb-testo"
                data-testid="feedback-testo"
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                placeholder="Es. Mi ha contattato un'agenzia di zona, molto precisa..."
                className="rounded-xl min-h-[80px]"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="feedback-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] text-white font-extrabold py-4 hover:bg-[#059669] transition-colors disabled:opacity-60"
            >
              {sending ? "Invio..." : "Invia il riscontro"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
